import React, { useState, useEffect, useRef, useMemo } from "react";
import { Banner, getOptimizedImageUrl } from "../types";
import {
  ChevronLeft,
  ChevronRight,
  Image as ImageIcon,
  Eye,
  Play,
  Pause,
  Monitor,
  Tablet as TabletIcon,
  Smartphone,
  X,
  Maximize2,
  Sparkles,
  ExternalLink,
  Layers,
  CheckCircle,
  HelpCircle,
} from "lucide-react";

export interface BigHeroBannerProps {
  banners: Banner[];
  autoSlideIntervalMs?: number;
  onBannerClick?: (banner: Banner) => void;
  isAdmin?: boolean;
  onOpenAdminBanners?: () => void;
  // Admin & Preview enhancements
  showPreviewButton?: boolean;
  isInAdminPanel?: boolean;
  draftBanner?: (Partial<Banner> & { imageUrl?: string }) | null;
  uncommittedBanners?: Banner[];
  onPreviewModeToggle?: (isOpen: boolean) => void;
  className?: string;
}

// Fallback high-resolution banners (without any text overlay) if admin hasn't added any yet
const DEFAULT_FALLBACK_BANNERS: Banner[] = [
  {
    id: "default-1",
    imageUrl: "https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=1400&q=80",
    order: 1,
    isActive: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: "default-2",
    imageUrl: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?auto=format&fit=crop&w=1400&q=80",
    order: 2,
    isActive: true,
    createdAt: new Date().toISOString(),
  },
  {
    id: "default-3",
    imageUrl: "https://images.unsplash.com/photo-1583847268964-b28dc8f51f92?auto=format&fit=crop&w=1400&q=80",
    order: 3,
    isActive: true,
    createdAt: new Date().toISOString(),
  },
];

type DeviceView = "desktop" | "tablet" | "mobile";

export const BigHeroBanner: React.FC<BigHeroBannerProps> = ({
  banners = [],
  autoSlideIntervalMs = 4500,
  onBannerClick,
  isAdmin = false,
  onOpenAdminBanners,
  showPreviewButton = true,
  isInAdminPanel = false,
  draftBanner = null,
  uncommittedBanners,
  onPreviewModeToggle,
  className = "",
}) => {
  // Modal / Preview Mode state
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [deviceView, setDeviceView] = useState<DeviceView>("desktop");
  const [previewClickFeedback, setPreviewClickFeedback] = useState<string | null>(null);

  // Compute the banners including any uncommitted draft
  const { displayBanners, hasDraft, draftBannerId } = useMemo(() => {
    let list: Banner[] = banners.map((b) => ({ ...b }));
    let hasDraftFlag = false;
    let draftId: string | null = null;

    if (uncommittedBanners && uncommittedBanners.length > 0) {
      list = [...uncommittedBanners];
      hasDraftFlag = true;
    } else if (draftBanner && draftBanner.imageUrl && draftBanner.imageUrl.trim()) {
      hasDraftFlag = true;
      draftId = draftBanner.id || "uncommitted-draft-temp";
      const draftObj: Banner = {
        id: draftId,
        imageUrl: draftBanner.imageUrl.trim(),
        title: draftBanner.title?.trim() || "Uncommitted Draft Banner",
        linkUrl: draftBanner.linkUrl?.trim() || "",
        order: typeof draftBanner.order === "number" ? draftBanner.order : list.length + 1,
        isActive: draftBanner.isActive !== undefined ? draftBanner.isActive : true,
        createdAt: draftBanner.createdAt || new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };

      // If draft matches an existing banner being edited, replace it in place
      const existingIdx = list.findIndex((b) => b.id === draftObj.id);
      if (existingIdx >= 0) {
        list[existingIdx] = draftObj;
      } else {
        list.push(draftObj);
      }
    }

    // Sort by order ascending
    list.sort((a, b) => (a.order || 0) - (b.order || 0));

    // Filter active banners
    const active = list.filter((b) => b.isActive);
    return {
      displayBanners: active.length > 0 ? active : DEFAULT_FALLBACK_BANNERS,
      hasDraft: hasDraftFlag,
      draftBannerId: draftId,
    };
  }, [banners, draftBanner, uncommittedBanners]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  // Swipe gesture tracking
  const touchStartXRef = useRef<number | null>(null);
  const touchEndXRef = useRef<number | null>(null);

  const totalSlides = displayBanners.length;

  // Auto-scroll loop
  useEffect(() => {
    if (totalSlides <= 1 || isPaused) return;

    const interval = Math.max(2500, autoSlideIntervalMs);
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, interval);

    return () => clearInterval(timer);
  }, [totalSlides, isPaused, autoSlideIntervalMs]);

  // Keep index within bounds
  useEffect(() => {
    if (currentIndex >= totalSlides && totalSlides > 0) {
      setCurrentIndex(0);
    }
  }, [totalSlides, currentIndex]);

  const handlePrev = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const handleNext = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchEndXRef.current = null;
    setIsPaused(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    setIsPaused(false);
    if (!touchStartXRef.current || !touchEndXRef.current) return;
    const diff = touchStartXRef.current - touchEndXRef.current;
    const swipeThreshold = 35;

    if (diff > swipeThreshold) {
      handleNext();
    } else if (diff < -swipeThreshold) {
      handlePrev();
    }
    touchStartXRef.current = null;
    touchEndXRef.current = null;
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    touchStartXRef.current = e.clientX;
    touchEndXRef.current = null;
    setIsPaused(true);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (touchStartXRef.current !== null) {
      touchEndXRef.current = e.clientX;
    }
  };

  const handlePointerUp = () => {
    handleTouchEnd();
  };

  const openPreviewModal = (e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    setIsPreviewModalOpen(true);
    if (onPreviewModeToggle) onPreviewModeToggle(true);
  };

  const closePreviewModal = () => {
    setIsPreviewModalOpen(false);
    if (onPreviewModeToggle) onPreviewModeToggle(false);
  };

  // Keyboard shortcut ESC to exit preview
  useEffect(() => {
    if (!isPreviewModalOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closePreviewModal();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isPreviewModalOpen]);

  const currentBanner = displayBanners[currentIndex % totalSlides];
  const isCurrentDraft = currentBanner && currentBanner.id === draftBannerId;

  // Banner height styles based on viewport context (compact 140-160px so products appear higher)
  const getBannerContainerHeight = (device: DeviceView) => {
    if (device === "mobile") return "h-[140px]";
    if (device === "tablet") return "h-[150px]";
    return "h-[140px] sm:h-[150px] md:h-[160px]";
  };

  return (
    <div className={`relative w-full ${className}`}>
      {/* Authenticated Admin Quick Controls - STRICTLY rendered only for authenticated admins, NEVER in public DOM */}
      {isAdmin && !isInAdminPanel && (
        <div className="absolute top-2.5 right-2.5 z-30 flex items-center gap-1.5 bg-slate-900/90 backdrop-blur-md px-2 py-1 rounded-xl border border-slate-700/80 shadow-md">
          {onOpenAdminBanners && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onOpenAdminBanners();
              }}
              className="px-2.5 py-1 text-[11px] font-sans font-bold text-white hover:text-sky-300 transition-colors flex items-center gap-1 cursor-pointer"
              title="Manage Banners in Admin Panel"
            >
              <span>Manage</span>
            </button>
          )}
          {showPreviewButton && (
            <button
              type="button"
              onClick={openPreviewModal}
              className="px-2.5 py-1 text-[11px] font-sans font-bold bg-[#0077B6] hover:bg-[#0096C7] text-white rounded-lg transition-colors flex items-center gap-1 cursor-pointer"
              title="Preview Banners Across Devices"
            >
              <Eye className="h-3 w-3" />
              <span>Preview Mode</span>
            </button>
          )}
        </div>
      )}

      {/* Admin Panel Header Bar (strictly embedded inside Admin Panel only) */}
      {isInAdminPanel && (
        <div className="flex flex-wrap items-center justify-between gap-2.5 pb-2.5 mb-2 px-1 border-b border-gray-100 dark:border-slate-800">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-xs font-black uppercase tracking-wider text-slate-800 dark:text-slate-200 font-sans">
              <Eye className="h-3.5 w-3.5 text-[#0077B6]" />
              Auto-Scroll Banner Preview
            </span>
            {hasDraft && (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-600 dark:text-amber-400 font-sans">
                <Sparkles className="h-3 w-3" />
                Uncommitted Draft Active
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Play/Pause state button */}
            <button
              type="button"
              onClick={() => setIsPaused((prev) => !prev)}
              className="px-2.5 py-1 text-[11px] font-sans font-bold rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 hover:bg-gray-100 text-gray-700 dark:text-gray-300 flex items-center gap-1 transition-colors cursor-pointer"
              title={isPaused ? "Resume auto-scroll" : "Pause auto-scroll"}
            >
              {isPaused ? <Play className="h-3 w-3 text-emerald-500" /> : <Pause className="h-3 w-3 text-amber-500" />}
              <span>{isPaused ? "Paused" : "Scrolling"}</span>
            </button>

            {/* Viewport Toggles (desktop, tablet, mobile) */}
            <div className="hidden sm:flex items-center p-0.5 rounded-lg bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700">
              <button
                type="button"
                onClick={() => setDeviceView("desktop")}
                className={`p-1.5 rounded-md text-[10px] font-bold flex items-center gap-1 cursor-pointer transition-colors ${
                  deviceView === "desktop"
                    ? "bg-white dark:bg-slate-700 text-[#0077B6] shadow-xs"
                    : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                }`}
                title="Desktop View (Full Width)"
              >
                <Monitor className="h-3 w-3" />
                <span>Desktop</span>
              </button>
              <button
                type="button"
                onClick={() => setDeviceView("tablet")}
                className={`p-1.5 rounded-md text-[10px] font-bold flex items-center gap-1 cursor-pointer transition-colors ${
                  deviceView === "tablet"
                    ? "bg-white dark:bg-slate-700 text-[#0077B6] shadow-xs"
                    : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                }`}
                title="Tablet View (768px)"
              >
                <TabletIcon className="h-3 w-3" />
                <span>Tablet</span>
              </button>
              <button
                type="button"
                onClick={() => setDeviceView("mobile")}
                className={`p-1.5 rounded-md text-[10px] font-bold flex items-center gap-1 cursor-pointer transition-colors ${
                  deviceView === "mobile"
                    ? "bg-white dark:bg-slate-700 text-[#0077B6] shadow-xs"
                    : "text-gray-500 hover:text-gray-900 dark:hover:text-white"
                }`}
                title="Mobile View (375px)"
              >
                <Smartphone className="h-3 w-3" />
                <span>Mobile</span>
              </button>
            </div>

            {/* Big Interactive 'Preview Mode' Button */}
            <button
              type="button"
              onClick={openPreviewModal}
              className="bg-gradient-to-r from-[#0077B6] to-[#0096C7] hover:from-[#005F92] hover:to-[#0077B6] text-white text-xs font-extrabold px-3 py-1.5 rounded-xl flex items-center gap-1.5 shadow-xs cursor-pointer transition-all hover:scale-102 active:scale-98"
              title="Launch Live Site Preview Mode Simulator"
            >
              <Eye className="h-3.5 w-3.5 text-sky-200" />
              <span>Preview Mode</span>
              <Maximize2 className="h-3 w-3 opacity-75" />
            </button>
          </div>
        </div>
      )}

      {/* Main Auto-Scroll Banner Display Container */}
      <div
        className={`mx-auto transition-all duration-300 ${
          isInAdminPanel && deviceView === "mobile"
            ? "max-w-[390px]"
            : isInAdminPanel && deviceView === "tablet"
            ? "max-w-[768px]"
            : "w-full"
        }`}
      >
        <div
          className={`relative w-full rounded-2xl overflow-hidden shadow-sm border border-slate-200/80 dark:border-slate-800 bg-slate-900 select-none group transition-all duration-300 ${getBannerContainerHeight(
            isInAdminPanel ? deviceView : "desktop"
          )}`}
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          style={{ touchAction: "pan-y" }}
        >
          {/* Full-bleed Auto-scrolling Images (No text overlays) */}
          <div className="relative w-full h-full overflow-hidden">
            {displayBanners.map((banner, idx) => {
              const isActive = idx === (currentIndex % totalSlides);
              const isDraft = banner.id === draftBannerId;

              return (
                <div
                  key={banner.id || idx}
                  onClick={() => {
                    if (isInAdminPanel) {
                      setPreviewClickFeedback(
                        banner.linkUrl
                          ? `Simulated link redirect: ${banner.linkUrl}`
                          : "Pure visual banner (no redirect link configured)"
                      );
                      setTimeout(() => setPreviewClickFeedback(null), 3000);
                      return;
                    }
                    if (onBannerClick && banner.linkUrl) {
                      onBannerClick(banner);
                    }
                  }}
                  className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out ${
                    isActive ? "opacity-100 z-10 pointer-events-auto" : "opacity-0 z-0 pointer-events-none"
                  } ${banner.linkUrl || isInAdminPanel ? "cursor-pointer" : "cursor-default"}`}
                  style={{ willChange: "opacity" }}
                >
                  <img
                    src={getOptimizedImageUrl(banner.imageUrl, 1200, 85)}
                    alt={banner.title || "Rentlow Promotional Banner"}
                    loading={idx === 0 ? "eager" : "lazy"}
                    decoding="async"
                    className="w-full h-full object-cover object-center transform transition-transform duration-700 ease-out group-hover:scale-102"
                  />

                  {/* Draft Badge if previewing uncommitted draft slide in admin */}
                  {isDraft && isInAdminPanel && (
                    <div className="absolute top-3 left-3 z-30 bg-amber-500/90 text-slate-950 font-black text-[10px] uppercase font-sans px-2 py-0.5 rounded-md shadow-lg backdrop-blur-md flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      <span>Uncommitted Draft</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Navigation Controls (Dots & Navigation Arrows) */}
          {totalSlides > 1 && (
            <>
              {/* Left Arrow */}
              <button
                type="button"
                onClick={handlePrev}
                className="absolute left-2 top-1/2 -translate-y-1/2 z-20 p-1.5 sm:p-2 rounded-full bg-black/40 hover:bg-black/75 text-white backdrop-blur-xs border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer active:scale-95"
                aria-label="Previous banner"
              >
                <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>

              {/* Right Arrow */}
              <button
                type="button"
                onClick={handleNext}
                className="absolute right-2 top-1/2 -translate-y-1/2 z-20 p-1.5 sm:p-2 rounded-full bg-black/40 hover:bg-black/75 text-white backdrop-blur-xs border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200 cursor-pointer active:scale-95"
                aria-label="Next banner"
              >
                <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
              </button>

              {/* Bottom Pagination Dots - Clearly visible with high contrast */}
              <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 bg-black/70 backdrop-blur-md px-3.5 py-1.5 rounded-full border border-white/30 shadow-lg">
                {displayBanners.map((_, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentIndex(idx);
                    }}
                    className={`h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                      (currentIndex % totalSlides) === idx
                        ? "w-7 bg-white shadow-md ring-2 ring-white/80"
                        : "w-2.5 bg-white/60 hover:bg-white/90"
                    }`}
                    aria-label={`Go to slide ${idx + 1}`}
                  />
                ))}
              </div>

              {/* Slide Counter in bottom right */}
              <div className="absolute bottom-2.5 right-3 z-20 hidden sm:flex items-center gap-1 text-[10px] font-sans font-bold bg-black/60 backdrop-blur-md px-2.5 py-0.5 rounded-full text-white border border-white/20">
                <span>{(currentIndex % totalSlides) + 1}</span>
                <span className="opacity-50">/</span>
                <span>{totalSlides}</span>
              </div>
            </>
          )}

          {/* Feedback banner if clicked in admin preview */}
          {previewClickFeedback && (
            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-40 bg-slate-900/90 text-sky-200 text-xs font-mono px-3 py-1.5 rounded-lg border border-sky-500/40 shadow-xl backdrop-blur-md animate-in fade-in zoom-in duration-150">
              {previewClickFeedback}
            </div>
          )}
        </div>
      </div>

      {/* ========================================================================= */}
      {/* 🌟 FULLSCREEN LIVE SITE PREVIEW MODE MODAL 🌟 */}
      {/* Allows administrators to see what the auto-scrolling images will look like */}
      {/* on the live site before committing changes to Firestore                     */}
      {/* ========================================================================= */}
      {isPreviewModalOpen && (isAdmin || isInAdminPanel) && (
        <div
          className="fixed inset-0 z-50 flex flex-col bg-slate-950/85 backdrop-blur-md animate-in fade-in duration-200 overflow-y-auto"
          role="dialog"
          aria-modal="true"
          aria-labelledby="preview-mode-title"
        >
          {/* Top Bar for Preview Simulator */}
          <div className="bg-slate-900/95 border-b border-slate-800 px-4 py-3 flex items-center justify-between gap-4 sticky top-0 z-20 shadow-md">
            <div className="flex items-center gap-3">
              <div className="p-1.5 rounded-lg bg-sky-500/10 border border-sky-500/30 text-sky-400">
                <Eye className="h-4 w-4" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 id="preview-mode-title" className="text-sm font-black text-white tracking-wide">
                    Live Site Preview Mode
                  </h2>
                  <span className="text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30">
                    REAL-TIME SIMULATION
                  </span>
                  {hasDraft && (
                    <span className="text-[10px] font-bold font-mono px-2 py-0.5 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      Uncommitted Changes Included
                    </span>
                  )}
                </div>
                <p className="text-[11px] text-slate-400">
                  Simulating live customer experience with auto-scroll speed ({autoSlideIntervalMs / 1000}s) and zero overlaid text.
                </p>
              </div>
            </div>

            {/* Viewport Selectors and Controls */}
            <div className="flex items-center gap-3">
              {/* Responsive Device Switcher */}
              <div className="flex items-center p-1 rounded-xl bg-slate-800/90 border border-slate-700/80">
                <button
                  type="button"
                  onClick={() => setDeviceView("desktop")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer ${
                    deviceView === "desktop"
                      ? "bg-[#0077B6] text-white shadow-xs"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  <Monitor className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Desktop</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDeviceView("tablet")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer ${
                    deviceView === "tablet"
                      ? "bg-[#0077B6] text-white shadow-xs"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  <TabletIcon className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Tablet</span>
                </button>
                <button
                  type="button"
                  onClick={() => setDeviceView("mobile")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer ${
                    deviceView === "mobile"
                      ? "bg-[#0077B6] text-white shadow-xs"
                      : "text-slate-400 hover:text-white"
                  }`}
                >
                  <Smartphone className="h-3.5 w-3.5" />
                  <span className="hidden sm:inline">Mobile</span>
                </button>
              </div>

              {/* Play / Pause Toggle */}
              <button
                type="button"
                onClick={() => setIsPaused((prev) => !prev)}
                className="px-3 py-1.5 rounded-xl border border-slate-700 bg-slate-800 text-xs font-bold font-mono text-slate-200 hover:bg-slate-700 flex items-center gap-1.5 cursor-pointer transition-colors"
              >
                {isPaused ? <Play className="h-3.5 w-3.5 text-emerald-400" /> : <Pause className="h-3.5 w-3.5 text-amber-400" />}
                <span className="hidden md:inline">{isPaused ? "Play Auto-Scroll" : "Pause Auto-Scroll"}</span>
              </button>

              {/* Close / Exit Button */}
              <button
                type="button"
                onClick={closePreviewModal}
                className="px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-rose-900/60 hover:border-rose-700 text-slate-300 hover:text-rose-200 border border-slate-700 text-xs font-bold flex items-center gap-1.5 transition-colors cursor-pointer"
                title="Exit Preview Mode (Esc)"
              >
                <X className="h-4 w-4" />
                <span>Exit Preview</span>
              </button>
            </div>
          </div>

          {/* Simulator Content Area */}
          <div className="flex-1 p-4 sm:p-8 flex flex-col items-center justify-start">
            {/* Mock Browser/Device Container */}
            <div
              className={`w-full bg-slate-900 rounded-2xl border border-slate-700 shadow-2xl overflow-hidden transition-all duration-300 ${
                deviceView === "mobile"
                  ? "max-w-[390px]"
                  : deviceView === "tablet"
                  ? "max-w-[768px]"
                  : "max-w-6xl"
              }`}
            >
              {/* Browser Window Chrome */}
              <div className="bg-slate-800/90 px-4 py-2.5 border-b border-slate-700/80 flex items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <div className="h-3 w-3 rounded-full bg-rose-500/80" />
                  <div className="h-3 w-3 rounded-full bg-amber-500/80" />
                  <div className="h-3 w-3 rounded-full bg-emerald-500/80" />
                </div>

                <div className="flex-1 max-w-md mx-auto bg-slate-950/60 border border-slate-700/60 rounded-lg px-3 py-1 text-[11px] font-mono text-slate-400 truncate text-center">
                  https://rentlow.com/ <span className="text-emerald-400">#hero-banners</span>
                </div>

                <div className="text-[10px] font-mono text-slate-400 font-bold uppercase">
                  {deviceView}
                </div>
              </div>

              {/* Mock Live Site Header Section */}
              <div className="bg-slate-950 p-4 border-b border-slate-800/80">
                <div className="flex items-center justify-between gap-4 pb-3">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded bg-[#0077B6] flex items-center justify-center text-white font-black text-xs">
                      R
                    </div>
                    <span className="font-extrabold text-white text-sm tracking-tight">RENTLOW</span>
                    <span className="hidden sm:inline text-[10px] text-slate-400 font-mono bg-slate-900 px-2 py-0.5 rounded border border-slate-800">
                      City: Lucknow
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-400 font-mono">
                    Slide {currentIndex + 1} of {totalSlides}
                  </div>
                </div>

                {/* THE ACTUAL LIVE BANNER COMPONENT UNDER TEST */}
                <div className="mt-1">
                  <div
                    className={`relative w-full rounded-2xl overflow-hidden shadow-sm border border-slate-700/80 bg-slate-950 select-none group transition-all duration-300 ${getBannerContainerHeight(
                      deviceView
                    )}`}
                    onMouseEnter={() => setIsPaused(true)}
                    onMouseLeave={() => setIsPaused(false)}
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                    onPointerDown={handlePointerDown}
                    onPointerMove={handlePointerMove}
                    onPointerUp={handlePointerUp}
                    style={{ touchAction: "pan-y" }}
                  >
                    {/* Images Cross-Fade */}
                    <div className="relative w-full h-full overflow-hidden">
                      {displayBanners.map((banner, idx) => {
                        const isActive = idx === (currentIndex % totalSlides);
                        const isDraft = banner.id === draftBannerId;

                        return (
                          <div
                            key={banner.id || idx}
                            className={`absolute inset-0 w-full h-full transition-opacity duration-700 ease-in-out ${
                              isActive ? "opacity-100 z-10 pointer-events-auto" : "opacity-0 z-0 pointer-events-none"
                            } ${banner.linkUrl ? "cursor-pointer" : "cursor-default"}`}
                            style={{ willChange: "opacity" }}
                            onClick={() => {
                              setPreviewClickFeedback(
                                banner.linkUrl
                                  ? `Redirect simulated: ${banner.linkUrl}`
                                  : "Pure visual banner (no redirect configured)"
                              );
                              setTimeout(() => setPreviewClickFeedback(null), 3000);
                            }}
                          >
                            <img
                              src={getOptimizedImageUrl(banner.imageUrl, 1200, 85)}
                              alt={banner.title || "Rentlow Promotional Banner"}
                              className="w-full h-full object-cover object-center transform transition-transform duration-700 ease-out group-hover:scale-102"
                            />

                            {/* Badge if this is the uncommitted draft */}
                            {isDraft && (
                              <div className="absolute top-3 left-3 z-30 bg-amber-500 text-slate-950 font-black text-[10px] uppercase font-mono px-2 py-0.5 rounded-md shadow-lg flex items-center gap-1">
                                <Sparkles className="h-3 w-3" />
                                <span>Uncommitted Draft Slide</span>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Navigation Arrows */}
                    {totalSlides > 1 && (
                      <>
                        <button
                          type="button"
                          onClick={handlePrev}
                          className="absolute left-2.5 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/45 hover:bg-black/80 text-white backdrop-blur-xs border border-white/15 cursor-pointer transition-all active:scale-95"
                          aria-label="Previous image"
                        >
                          <ChevronLeft className="h-5 w-5" />
                        </button>
                        <button
                          type="button"
                          onClick={handleNext}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 z-20 p-2 rounded-full bg-black/45 hover:bg-black/80 text-white backdrop-blur-xs border border-white/15 cursor-pointer transition-all active:scale-95"
                          aria-label="Next image"
                        >
                          <ChevronRight className="h-5 w-5" />
                        </button>

                        {/* Pagination Dots */}
                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5 bg-black/40 backdrop-blur-md px-3 py-1 rounded-full border border-white/10">
                          {displayBanners.map((_, idx) => (
                            <button
                              key={idx}
                              type="button"
                              onClick={() => setCurrentIndex(idx)}
                              className={`h-2 rounded-full transition-all duration-300 cursor-pointer ${
                                (currentIndex % totalSlides) === idx
                                  ? "w-6 bg-white shadow-xs"
                                  : "w-2 bg-white/40 hover:bg-white/70"
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}

                    {/* Temporary Click Feedback */}
                    {previewClickFeedback && (
                      <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-40 bg-slate-900/95 text-sky-200 text-xs font-mono px-3.5 py-1.5 rounded-lg border border-sky-500/50 shadow-2xl backdrop-blur-md">
                        {previewClickFeedback}
                      </div>
                    )}
                  </div>
                </div>

                {/* Simulated Customer Experience Info */}
                <div className="mt-4 p-3 rounded-xl bg-slate-900 border border-slate-800 text-xs flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-slate-400">
                  <div className="flex items-center gap-2">
                    <span className="font-bold text-slate-300">Slide #{currentIndex + 1}:</span>
                    <span className="text-slate-200 font-medium truncate max-w-xs sm:max-w-md">
                      {currentBanner?.title || `Banner ${currentIndex + 1}`}
                    </span>
                    {isCurrentDraft && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 font-mono text-[10px] font-bold">
                        Draft (Unsaved)
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 text-[11px] font-mono">
                    <span>
                      Speed: <strong>{(autoSlideIntervalMs / 1000).toFixed(1)}s</strong>
                    </span>
                    <span>
                      Status:{" "}
                      <strong className={isPaused ? "text-amber-400" : "text-emerald-400"}>
                        {isPaused ? "Paused (Hover/User)" : "Auto-Scrolling"}
                      </strong>
                    </span>
                  </div>
                </div>
              </div>

              {/* Mock Live Site Body Placeholder to simulate real page context */}
              <div className="p-4 sm:p-6 bg-slate-950/60 text-slate-600 text-xs space-y-3">
                <div className="flex items-center justify-between">
                  <div className="h-4 w-32 bg-slate-800/80 rounded animate-pulse" />
                  <div className="h-4 w-20 bg-slate-800/80 rounded animate-pulse" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-24 bg-slate-900 rounded-xl border border-slate-800/80 p-2 flex flex-col justify-end">
                      <div className="h-3 w-3/4 bg-slate-800 rounded mb-1.5" />
                      <div className="h-2.5 w-1/2 bg-slate-800/60 rounded" />
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Bottom helper text */}
            <div className="mt-4 text-center text-xs text-slate-400 flex items-center justify-center gap-2">
              <HelpCircle className="h-3.5 w-3.5 text-slate-500" />
              <span>
                Changes are simulated live. Once you are satisfied with the look and timing, click <strong>"Save & Add"</strong> or{" "}
                <strong>"Update Banner"</strong> in the admin form to commit changes.
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BigHeroBanner;
